import { ATTR_PREFIX, HOST_ID } from './contract';
import type { TextSize } from './prefs';
import { CONTROL_TAGS, SKIP_TAGS } from './text-blocks';

/**
 * Text size. School designs mostly set font sizes in px, so a root font-size
 * change would do nothing. Instead each element that holds text gets its own
 * computed size multiplied, as an inline `!important` px value.
 *
 * Levels never compound: the original computed size and the original inline
 * value are recorded the first time an element is scaled, every level change
 * restores all of them first, and all reads happen before any write.
 *
 * Known limit (ticket §6): an element inserted later *inside* an element that
 * is already scaled reads the scaled size as its own and can scale twice.
 */

export const TEXT_SIZE_FACTORS: Record<TextSize, number> = {
  0: 1,
  1: 1.15,
  2: 1.3,
  3: 1.5,
};

interface Original {
  inline: string;
  priority: string;
}

let level: TextSize = 0;
const originals = new WeakMap<HTMLElement, Original>();
const scaled = new Set<HTMLElement>();
let observer: MutationObserver | null = null;
let pending: Element[] = [];
let frame = 0;

export function currentTextSize(): TextSize {
  return level;
}

export function setTextSize(next: TextSize): void {
  if (next === level) return;
  restoreAll();
  level = next;
  const root = document.documentElement;
  if (next === 0) {
    root.removeAttribute(`${ATTR_PREFIX}text`);
    stopObserving();
    return;
  }
  root.setAttribute(`${ATTR_PREFIX}text`, String(next));
  if (document.body) scaleElements(collect(document.body));
  startObserving();
}

function hasOwnText(el: Element): boolean {
  for (let node = el.firstChild; node; node = node.nextSibling) {
    if (node.nodeType === 3 && node.nodeValue && node.nodeValue.trim() !== '')
      return true;
  }
  return false;
}

function isScalable(el: Element): el is HTMLElement {
  if (!(el instanceof HTMLElement)) return false; // skips SVG and MathML
  if (SKIP_TAGS.has(el.tagName) || el.id === HOST_ID) return false;
  return CONTROL_TAGS.has(el.tagName) || hasOwnText(el);
}

function collect(root: Element): HTMLElement[] {
  const found: HTMLElement[] = [];
  if (isScalable(root) && !scaled.has(root)) found.push(root);
  const all = root.getElementsByTagName('*');
  for (let i = 0; i < all.length; i++) {
    const el = all[i];
    if (isScalable(el) && !scaled.has(el)) found.push(el);
  }
  return found;
}

function scaleElements(candidates: HTMLElement[]): void {
  // A parent and its child can both arrive in one mutation batch; scaling the
  // child twice would record our own value as its "original".
  const elements = Array.from(new Set(candidates)).filter(
    (el) => !scaled.has(el),
  );
  if (elements.length === 0) return;
  const factor = TEXT_SIZE_FACTORS[level];
  // Read everything first, then write, so no read sees a size this pass set.
  const reads = elements.map((el) => ({
    px: parseFloat(window.getComputedStyle(el).fontSize),
    inline: el.style.getPropertyValue('font-size'),
    priority: el.style.getPropertyPriority('font-size'),
  }));
  elements.forEach((el, i) => {
    const { px, inline, priority } = reads[i];
    if (!isFinite(px) || px <= 0) return;
    originals.set(el, { inline, priority });
    scaled.add(el);
    el.style.setProperty(
      'font-size',
      `${Math.round(px * factor * 100) / 100}px`,
      'important',
    );
  });
}

function restoreAll(): void {
  scaled.forEach((el) => {
    const original = originals.get(el);
    if (original && original.inline) {
      el.style.setProperty('font-size', original.inline, original.priority);
    } else {
      el.style.removeProperty('font-size');
    }
    originals.delete(el);
  });
  scaled.clear();
}

function startObserving(): void {
  if (observer || typeof MutationObserver === 'undefined' || !document.body)
    return;
  observer = new MutationObserver((records) => {
    records.forEach((record) => {
      record.addedNodes.forEach((node) => {
        if (node.nodeType === 1) pending.push(node as Element);
      });
    });
    if (pending.length && !frame) frame = window.requestAnimationFrame(flush);
  });
  // childList only: our own style writes are attribute changes and never re-trigger this.
  observer.observe(document.body, { childList: true, subtree: true });
}

function stopObserving(): void {
  if (observer) observer.disconnect();
  observer = null;
  pending = [];
  if (frame) window.cancelAnimationFrame(frame);
  frame = 0;
}

function flush(): void {
  frame = 0;
  const batch = pending;
  pending = [];
  if (level === 0) return;
  const elements: HTMLElement[] = [];
  batch.forEach((root) => {
    if (root.isConnected) collect(root).forEach((el) => elements.push(el));
  });
  scaleElements(elements);
  // Drop elements that left the page, so a long visit on a busy page does not grow forever.
  if (scaled.size > 2000) {
    scaled.forEach((el) => {
      if (!el.isConnected) scaled.delete(el);
    });
  }
}
